import React, {
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
  useRef
} from 'react'
import { useSelector } from 'react-redux'

import _ from 'lodash'
import shortid from 'shortid';

import { WidgetsContext, GatingContext, ModalContext } from './../core/context'
import { Widgets } from './../core'
import { MODAL, SLIDE } from '../../../constants/widgetTypes'
import {
  useFormSuccessSelector,
  useOtpFormSuccessSelector,
  useOtpRequiredSelector,
  usePageDuration, usePagePercentage,
} from '../core/hooks'
import { Fonts } from '@paperflite-marketing/cleverstory-commons'
import { SpatialPage } from '../spatialContent'

export const GatingManager = React.forwardRef(
  (
    {
      configurations,
      children,
      resetGating,
      page,
      breakpoint,
      appearance,
      storyboardType,
      preview,
      onGatingClose, 
      triggerGatingInteraction, 
      pageSession, 
      spatialSmartGating },
    ref
  ) => {
    const context = React.useContext(WidgetsContext)

    const [gatingContext, setGatingContext] = React.useState({
      preview: preview,
      disabled: false,
      triggerGating: () => {},
      type: null,
      payload: null,
      gating: null,
      configuration: null,
      configurations: [],
      assetConfigurations: []
    })

    const [widgetContextState, setWidgetContextState] = React.useState({
      ...context,
      eventUid: '',
      gatingTrigger: null,
      smart: false
    })

    var formSuccess = useFormSuccessSelector(widgetContextState.selector)
    var otpFormSuccess = useOtpFormSuccessSelector(widgetContextState.selector)
    var otpRequired = useOtpRequiredSelector(widgetContextState.selector)
    const activeSlide = useSelector((state) =>
      _.get(state, `pages.spatial.activePage`, null)
    )
    const spatialPages = useSelector(state => _.get(state, 'pages.spatial.items', []));
    const activeSpatialPage = spatialPages.find(page => page.id === activeSlide);
    const eventUidRef = useRef('');

    const [exitIntentTriggered, setExitIntentTriggered] = useState(false);
    const exitIntentListenerRef = useRef(null);

    const duration = usePageDuration(page, storyboardType)
    const percentage = usePagePercentage(page, storyboardType);

    const smartSpatialDuration = usePageDuration(page, storyboardType, true);
    const smartSpatialPercentage = usePagePercentage(page, storyboardType, true);

    const assetType =
      _.get(gatingContext, 'configuration.target.entityType', '') === 'asset'

    useState(() => {
      setGatingContext((gatingContext) => {
        return {
          ...gatingContext,
          triggerGating: triggerGating
        }
      })
    }, [])

    React.useEffect(() => {
      if (gatingContext.gating) {
        setWidgetContextState((widgetContextState) => {
          return {
            ...widgetContextState,
            ...context.createGatingContext(gatingContext.gating)
          }
        })
      }
    }, [gatingContext.gating])

    useEffect(() => {
      initiateGatingForTimeSpent()
    }, [duration, smartSpatialDuration])

    useEffect(() => {
      setupExitIntentListener()
      return () => {
        cleanupExitIntentListener()
      }
    }, [gatingContext.configurations, duration, gatingContext.gating, gatingContext.disabled, preview])

    useEffect(() => {
      setGatingContext((gatingContext) => {
        return {
          ...gatingContext,
          configurations: configurations
        }
      })
    }, [configurations])

    useEffect(() => {
      if (formSuccess && !otpRequired) {
        handleFormSuccess()
      }
    }, [formSuccess])

    useEffect(() => {
      if (otpFormSuccess && otpRequired) {
        handleFormSuccess()
      }
    }, [otpFormSuccess])

    useEffect(() => {
      if (context) {
        setWidgetContextState((widgetContextState) => {
          return {
            ...context,
            ...widgetContextState
          }
        })
      }
    }, [context])

    React.useImperativeHandle(ref, () => ({
      triggerGating: triggerGating,
      closeGating: handleClose
    }))

    const triggerGating = (type, payload, configuration) => {

      if (gatingContext.gating || gatingContext.disabled || (storyboardType === 'SPATIAL' && type === 'PREVIEW' && activeSlide !== page))  {
        return
      }

      var gating = _.get(configuration, 'gating.id')

      setGatingContext((gatingContext) => {
        return {
          ...gatingContext,
          gating: gating,
          type: type,
          payload: payload,
          configuration: configuration
        }
      })
    }

    const initiateGatingForTimeSpent = () => {

      if (gatingContext.gating || gatingContext.disabled || preview || _.isEmpty(gatingContext.configurations)) {
        return
      }

      var gatingConfigurations = gatingContext.configurations?.filter((item) => {
        return _.get(item, 'trigger.type', null) === 'TIME' || _.get(item, 'trigger.type', null) === 'PERCENTAGE'
      })

      gatingConfigurations.every((item) => {
        var triggerType = _.get(item, 'trigger.type', null);
        var triggerValue = _.get(item, 'trigger.value', 0);
        var isSmartGating = _.get(item, 'smart', false) == true;
        const targetValue = _.get(item, 'target', null);
        const isSmartSpatialGating = storyboardType === 'SPATIAL' && isSmartGating;
        const conditionalDuration = isSmartSpatialGating ? smartSpatialDuration : duration;
        const conditionalPercentage = isSmartSpatialGating ? smartSpatialPercentage: percentage;

        if (isSmartSpatialGating) {
          _.set(item, 'target', activeSpatialPage || targetValue);
        }

        if ((triggerType === 'TIME' && triggerValue <= conditionalDuration) || (triggerType === 'PERCENTAGE' && triggerValue <= conditionalPercentage)) {
          var gating = _.get(item, 'gating.id', null)
          var target = _.get(item, 'target.id', null)
          var targetCanonical = _.get(item, 'target.canonical', null)

          const isValidGating = storyboardType === 'SPATIAL' ? gating && target === activeSlide && page === targetCanonical  : gating;

          if(isValidGating) {
            if (isSmartSpatialGating) {
              spatialSmartGating(_.get(item, 'id', null))
            }
            setGatingContext((gatingContext) => {
              return {
                ...gatingContext,
                gating: gating,
                type: _.get(item, 'trigger.type', null),
                payload: null,
                configuration: item
              }
            })

            return false
          }
        }

        return true
      })
    }

    const setupExitIntentListener = () => {
      cleanupExitIntentListener();
      
      if (gatingContext.gating || gatingContext.disabled || preview || _.isEmpty(gatingContext.configurations) || duration < 3) {
        return
      }

      const exitIntentConfigurations = gatingContext.configurations.filter((item) => {
        return _.get(item, 'trigger.type', null) === 'EXIT_INTENT'
      })

      if (exitIntentConfigurations.length === 0) {
        return
      }
    
      let mouseY = 0;
      let mouseVelocityY = 0;
      let lastMouseY = 0;
      let lastTime = Date.now();
    
      const mouseMoveHandler = (e) => {
        const currentTime = Date.now();
        const timeDelta = currentTime - lastTime;
        
        if (timeDelta > 0) {
          mouseVelocityY = (e.clientY - lastMouseY) / timeDelta;
        }
        
        mouseY = e.clientY;
        lastMouseY = e.clientY;
        lastTime = currentTime;
      }
    
      const exitIntentHandler = (e) => {
        const isMovingUp = mouseVelocityY < -0.5;
        const isNearTop = e.clientY <= 15;
        const isLeavingViewport = e.type === 'mouseleave' && mouseY <= 20;
    
        if ((isNearTop && isMovingUp) || (e.clientY <= 0 || e.screenY <= 3 || e.pageY <= 0) || isLeavingViewport) {
          if (!exitIntentTriggered && duration >= 3) {
            handleExitIntent(e, exitIntentConfigurations);
          }
        }
      }
    
      exitIntentListenerRef.current = {
        mouseMoveHandler: mouseMoveHandler,
        exitIntentHandler: exitIntentHandler
      };
    
      document.addEventListener('mousemove', mouseMoveHandler, { passive: true });
      document.addEventListener('mouseleave', exitIntentHandler, { passive: true });
      document.addEventListener('mouseout', exitIntentHandler, { passive: true });
      window.addEventListener('mouseout', exitIntentHandler, { passive: true });
    }

    const cleanupExitIntentListener = () => {
      if (exitIntentListenerRef.current) {
        const { mouseMoveHandler, exitIntentHandler } = exitIntentListenerRef.current;
        
        document.removeEventListener('mousemove', mouseMoveHandler);
        document.removeEventListener('mouseleave', exitIntentHandler);
        document.removeEventListener('mouseout', exitIntentHandler);
        window.removeEventListener('mouseout', exitIntentHandler);
        
        exitIntentListenerRef.current = null;
      }
    }

    const handleExitIntent = (e, exitIntentConfigurations) => {
      if (exitIntentTriggered || duration < 3) {
        return;
      }
    
      setExitIntentTriggered(true);
      setTimeout(() => {
        exitIntentConfigurations.every((item) => {
          var isSmartGating = _.get(item, 'smart', false) == true;
          const targetValue = _.get(item, 'target', null);
          const isSmartSpatialGating = storyboardType === 'SPATIAL' && isSmartGating;
          if (isSmartSpatialGating) {
            _.set(item, 'target', activeSpatialPage || targetValue);
          }
          const conditionalDuration = isSmartSpatialGating ? smartSpatialDuration : duration;
          var gating = _.get(item, 'gating.id', null)
          var target = _.get(item, 'target.id', null)
          var targetCanonical = _.get(item, 'target.canonical', null)
    
          const isValidGating = storyboardType === 'SPATIAL' ? 
            gating && target === activeSlide && page === targetCanonical : 
            gating;
    
          if (isValidGating && conditionalDuration > 3) {
            setGatingContext((gatingContext) => {
              return {
                ...gatingContext,
                gating: gating,
                type: 'EXIT_INTENT',
                payload: null,
                configuration: item
              }
            })

            cleanupExitIntentListener()
            return false
          }

          return true
        })
      }, 50);
    }

    const isFullScreenVideo = () => {
      const fullscreenContainer = document.fullscreenElement
      const videoElement = fullscreenContainer
        ? fullscreenContainer.querySelector('video')
        : null
      const modalElement =
        document.getElementsByClassName('ReactModalPortal')[0]
      if (videoElement) {
        fullscreenContainer.removeChild(modalElement)
      }
    }

  const handleTriggerGatingInteraction=(type)=>{
    if(preview) {
      return;
    }

    switch (type) {
      case 'CLOSE':
        triggerGatingInteraction({
          gating: gatingContext.gating,
          gatingConfiguration: _.get(gatingContext.configuration, "id", null),
          gatingTrigger: _.get(gatingContext.configuration, 'trigger', null),
          modalEvent: "MODAL_CLOSED",
          name: "Gating Closed",
          eventUid: eventUidRef.current
        })
        break;

      case 'OPEN':
        const uid = `form_${shortid.generate()}`;
        eventUidRef.current = uid; 
        setWidgetContextState(prevState => ({
          ...prevState,
          eventUid: uid,
          gatingTrigger: _.get(gatingContext.configuration, 'trigger', null),
          smart: _.get(gatingContext.configuration, 'smart', false)
        }));   
        triggerGatingInteraction({
          gating: gatingContext.gating,
          gatingConfiguration: _.get(gatingContext.configuration, "id", null),
          gatingTrigger: _.get(gatingContext.configuration, 'trigger', null),
          modalEvent: "MODAL_OPENED",
          name: "Gating Opened",
          eventUid: uid
        })
        break;
    }
  }

    const handleFormSuccess = () => {
      if (!preview) {
        if (gatingContext.type === 'INTERACTIVE_DOWNLOAD') {
          if (gatingContext.payload.download) {
            gatingContext.payload.download()
          }
        }
      }
      isFullScreenVideo()
      resetGating(page, false)

      cleanupExitIntentListener()

      setGatingContext((gatingContext) => {
        if (preview) {
          return {
            ...gatingContext,
            gating: null,
            configuration: null
          }
        }

        return {
          ...gatingContext,
          triggerGating: () => {},
          type: null,
          payload: null,
          gating: null,
          configuration: null,
          configurations: [],
          disabled: true
        }
      })
    }

    const handleClose = (configuration, switchGating = false) => {
      if(!configuration) {
        return;
      }
      var items = [...gatingContext.assetConfigurations, configuration.id]

    setGatingContext((gatingContext) => {
      return {
        ...gatingContext,
        gating: null,
        configuration: null,
        configurations: (gatingContext.configurations || []).filter(item => item.id !== configuration.id),
        assetConfigurations: items
      }
    });

    isFullScreenVideo();
    resetGating(page, switchGating);

    handleTriggerGatingInteraction('CLOSE');

    cleanupExitIntentListener()

    if(assetType && !configuration?.optional) {
      onGatingClose();
    }
  }

    return (
      <GatingContext.Provider ref={ref} value={gatingContext}>
        {gatingContext.gating && (pageSession || preview) && (
          <GatingRender
            widgetContext={widgetContextState}
            appearance={appearance}
            configuration={gatingContext.configuration}
            gatingId={gatingContext.gating}
            show={gatingContext.gating !== null}
            onClose={handleClose}
          handleTriggerGatingInteraction={handleTriggerGatingInteraction}
            breakpoint={breakpoint}
            assetType={assetType}
            preview={preview}
          />
        )}
        {children}
      </GatingContext.Provider>
    )
  }
)

const GatingRender = React.memo(({
  widgetContext,
  configuration,
  gatingId,
  appearance,
  show,
  onClose,
  assetType,
  breakpoint,
  preview, handleTriggerGatingInteraction
}) => {
  const [modalContext, setModalContext] = React.useState({})
  const gating = useSelector((state) =>
    _.get(state, `gatings.gating[${gatingId}].item`, null)
  )
  const activeSlide = useSelector((state) =>
    _.get(state, `pages.spatial.activePage`, null)
  )

  var widgets = _.get(gating, 'draft.widgets', [])
  if (!preview) {
    widgets = _.get(gating, 'content.widgets', [])

    const fonts = _.get(gating, `content.fonts`) || []
    Fonts.useFont(fonts)
  }

  useEffect(() => {
    if (widgetContext.onTriggerMixpanelEvent) {
      widgetContext.onTriggerMixpanelEvent(
        widgetContext.session,
        'Page Viewed',
        {
          screen_name: 'Gating Form'
        }
      )
    }

    handleTriggerGatingInteraction('OPEN')

  }, [])

  const modalWidget = widgets.find(
    (item) => item.type === MODAL || item.type === SLIDE
  )
  const assetModal = _.get(configuration, 'target.assetModal', null)



  useEffect(() => {
    if (configuration?.optional || (assetType && assetModal)) {
      setModalContext((modalContext) => {
        return {
          ...modalContext,
          close: handleCloseGating
        }
      })
    }
  }, [configuration, assetType, assetModal])

  const handleCloseGating = () => {
    if (onClose) {
      onClose(configuration)
    }
  }

  if (!modalWidget) {
    return null
  }

  return (
    <WidgetsContext.Provider value={widgetContext}>
      <ModalContext.Provider value={modalContext}>
      { modalWidget.type === SLIDE ?
        <SpatialPage preview={preview} appearance={appearance} page={{id:activeSlide}}>
          <Widgets
            key={modalWidget.uid}
            widgetProps={modalWidget}
            breakpoint={breakpoint}
            show={show}
          />
        </SpatialPage>
        :
          <Widgets
            key={modalWidget.uid}
            widgetProps={modalWidget}
            breakpoint={breakpoint}
            show={show}
          />
      }
      </ModalContext.Provider>
    </WidgetsContext.Provider>
  )
})
